import { INVALID_DATA_ERROR } from "../components/MerchandisingSets/MerchandizingSetUpload/MSUploadModal.constant";
import { ToastMessage } from "./Toast/ToastMessage";
import {
  BAD_REQUEST,
  INVALID_CHAR_2,
  NO_RESPONSE_ERROR,
} from "../components/OutletGeoCompliance/OGComplianceBulkUpload/BulkUpdateModal.constant";
import { ValidateExcel } from "./PosmOutletValidateExcel";
import { ValidateSurveyOutletExcel } from "./SurveyOutletValidateExcel";
import { ValidateSosOutletExcel } from "./SosOutletValidateExcel";

export const checkUploadError = (
  err,
  setFileError,
  resetUploadState,
  t,
  handleClose,
) => {
  const isRowError = err?.shortDescription;

  if (isRowError) {
    setFileError({
      header: INVALID_DATA_ERROR,
      message: err.shortDescription,
    });
    resetUploadState();
  } else {
    ToastMessage(false, t("UploadError"));
    handleClose();
  }
};

export const handleUploadException = (
  error,
  setFileError,
  setNetworkStatus,
  resetUploadState,
  t,
  handleClose,
) => {
  if (JSON.stringify(error) === "{}" && !error.errors) {
    setFileError({ header: BAD_REQUEST, message: NO_RESPONSE_ERROR });
    setNetworkStatus(false);
  } else {
    checkUploadError(
      error.errors,
      setFileError,
      resetUploadState,
      t,
      handleClose,
    );
  }
};

export const processUploadResponse = (
  response,
  t,
  setRefreshTable,
  handleClose,
  setFileError,
  resetUploadState,
  message,
) => {
  if (response.statusCode === 200 && response["response-data"]) {
    ToastMessage(true, t(message));
    setRefreshTable(true);
    handleClose();
  } else {
    checkUploadError(
      response?.errors,
      setFileError,
      resetUploadState,
      t,
      handleClose,
    );
  }
};

export const getFormattedFileName = (fileName, isImageUpload, time) => {
  if (!fileName) return "";

  const baseName = fileName.replace(/\.(csv|zip)$/i, "");
  const extension = isImageUpload ? "zip" : "csv";
  return isImageUpload
    ? `${baseName}.${extension}`
    : `${baseName}_${time}.${extension}`.replace(/ /g, "_");
};

export const getMonthName = (index) => {
  return new Date(2000, index, 1).toLocaleString("default", { month: "long" });
};

export const getMonthTabs = () => {
  const today = new Date();
  const currentMonth = today.getMonth(); // 0-indexed: Jan = 0, Dec = 11
  const currentMonthName = getMonthName(
    new Date(today.getFullYear(), currentMonth, 1).getMonth(),
  );
  const months = [];
  for (let offset = -1; offset <= 2; offset++) {
    const date = new Date(today.getFullYear(), currentMonth + offset, 1);
    months.push({
      label:
        currentMonthName === getMonthName(date.getMonth())
          ? getMonthName(date.getMonth()) + "(Current)"
          : getMonthName(date.getMonth()),
      key: getMonthName(date.getMonth()) + " " + date.getFullYear(), // Month YYYY
    });
  }

  return months;
};

export const getDownloadMonthYearFromSelectedTab = (
  selectedTab,
  monthTabs = getMonthTabs(),
  referenceDate = new Date(),
) => {
  const selectedTabIndex = monthTabs.findIndex(
    (tab) => tab.key === selectedTab,
  );
  const monthOffset = selectedTabIndex === -1 ? 0 : selectedTabIndex - 1;
  const resolvedDate = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth() + monthOffset,
    1,
  );

  return {
    month: resolvedDate.getMonth() + 1,
    year: resolvedDate.getFullYear(),
  };
};

export const buildDownloadSetsFileName = (month, year) => {
  const monthName = new Date(year, month - 1, 1).toLocaleString("default", {
    month: "long",
  });
  return `Merchandizing Sets ${monthName} ${year}.csv`;
};

// Tab keys are sent to the API (e.g. downloadSets?section=Live), so only labels may change.
export const ID_TABS = {
  PAST: "Past",
  LIVE: "Live",
  UPCOMING: "Upcoming",
};

export const getTabsforID = () => {
  const allTabs = [
    { label: "Past", key: ID_TABS.PAST },
    { label: "Current", key: ID_TABS.LIVE },
    { label: "Upcoming", key: ID_TABS.UPCOMING },
  ];
  return allTabs;
};

export const STEP_STATUS = {
  COMPLETE: "complete",
  ONGOING: "ongoing",
  LIVE: "live",
};

export const CONFIGURE_CTA = {
  CREATE_SETS: "createSets",
  CREATE_DISPLAYS: "createDisplays",
  ADD_IMAGES: "addImages",
  UPLOAD_OUTLETS: "uploadOutlets",
};

const BUILD_SETS_STEP = {
  key: "buildSets",
  label: "BuildSets",
  ctas: [CONFIGURE_CTA.CREATE_SETS, CONFIGURE_CTA.CREATE_DISPLAYS, CONFIGURE_CTA.ADD_IMAGES],
};

const UPLOAD_OUTLETS_STEP = {
  key: "uploadEligibleOutlets",
  label: "UploadEligibleOutlets",
  ctas: [CONFIGURE_CTA.UPLOAD_OUTLETS],
};

const LIVE_STEP = {
  key: "live",
  label: "LiveStep",
  ctas: [],
};

// Steps whose status is NA for a tab are left out of that tab's timeline.
const ID_CONFIGURE_STEPS = {
  [ID_TABS.PAST]: [],
  [ID_TABS.LIVE]: [
    { ...BUILD_SETS_STEP, status: STEP_STATUS.COMPLETE },
    { ...UPLOAD_OUTLETS_STEP, status: STEP_STATUS.COMPLETE },
    { ...LIVE_STEP, status: STEP_STATUS.LIVE },
  ],
  [ID_TABS.UPCOMING]: [
    { ...BUILD_SETS_STEP, status: STEP_STATUS.ONGOING },
    { ...UPLOAD_OUTLETS_STEP, status: STEP_STATUS.ONGOING },
  ],
};

export const getConfigureStepsForID = (tabKey) => ID_CONFIGURE_STEPS[tabKey] ?? [];

export const getDefaultConfigureStep = (steps) => {
  const step = steps.find((s) => s.status === STEP_STATUS.ONGOING) ?? steps[steps.length - 1];
  return step?.key ?? null;
};

export const formatTodayLabel = (date = new Date()) =>
  date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export const isActiveCoulumnVisible = (selectedTab) => {
  let months = getMonthTabs();
  return selectedTab !== months[3].key;
};

export const isLessThanToday = (dateStr) => {
  const todayStr = new Date().toISOString().split("T")[0];
  const targetStr = new Date(dateStr).toISOString().split("T")[0];
  return todayStr > targetStr;
};

const validateTypeAndChars = (value, rowNum, INVALID) => {
  if (typeof value !== "string") {
    return INVALID(
      `Invalid overlapping conditions in row ${rowNum}${INVALID_CHAR_2}`,
    );
  }

  const validCharsRegex = /^[a-zA-Z0-9_/*()|\s]+$/;
  if (!validCharsRegex.test(value)) {
    return INVALID(`Invalid characters in row ${rowNum}${INVALID_CHAR_2}`);
  }

  return null;
};

const validateParentheses = (value, rowNum, INVALID) => {
  const openParens = (value.match(/\(/g) || []).length;
  const closeParens = (value.match(/\)/g) || []).length;

  if (openParens !== closeParens) {
    return INVALID(`Mismatched parentheses in row ${rowNum}`);
  }

  if (/\([^()]*\([^()]*\)[^()]*\)/.test(value)) {
    return INVALID(`Nested parentheses not allowed in row ${rowNum}`);
  }

  if (openParens > 7) {
    return INVALID(
      `More than 7 parentheses pairs not allowed in row ${rowNum}`,
    );
  }

  return null;
};

const validateOperators = (value, rowNum, INVALID) => {
  if (/\*\*|\|\||\*\||\|\*/.test(value)) {
    return INVALID(`Consecutive operators not allowed in row ${rowNum}`);
  }
  return null;
};

const validateTotalSets = (value, rowNum, INVALID) => {
  // Split by operators (* or |)
  const sets = value
    .split(/[|*]/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (sets.length > 7) {
    return INVALID(`More than 7 sets are not allowed in row ${rowNum}`);
  }

  return null;
};

const validateSetDate = (dateStr) => {
  const [day, month, year] = dateStr.split("/").map(Number);
  const setDate = new Date(year, month - 1, day);
  const today = new Date();
  const targetMonth = (today.getMonth() + 2) % 12;
  const targetYear =
    today.getFullYear() + Math.floor((today.getMonth() + 2) / 12);
  return (
    setDate.getMonth() === targetMonth && setDate.getFullYear() === targetYear
  );
};

const validateSetFormat = (
  setPattern,
  validateSetDate,
  group,
  rowNum,
  INVALID,
) => {
  const innerSets = group
    .slice(1, -1)
    .split(/[|*]/) // safe: no backtracking
    .map((s) => s.trim())
    .filter(Boolean);

  for (const s of innerSets) {
    const trimmed = s.trim();
    if (!setPattern.test(trimmed)) {
      return INVALID(`Invalid set format: "${trimmed}" in row ${rowNum}`);
    }

    const dateMatch = trimmed.match(/\d{2}\/\d{2}\/\d{4}$/);
    if (!dateMatch || !validateSetDate(dateMatch[0])) {
      return INVALID(
        `Set date out of allowed range (current month + 2): "${trimmed}" in row ${rowNum}`,
      );
    }
  }

  return null;
};

export const validateOverlappingConditionsHelper = (
  overlappingValue,
  rowNum,
  INVALID,
) => {
  const value = overlappingValue.toString().trim();

  // --- Type + allowed chars check ---
  let error = validateTypeAndChars(value, rowNum, INVALID);
  if (error) return error;

  // --- Parentheses checks ---
  error = validateParentheses(value, rowNum, INVALID);
  if (error) return error;

  // --- Operator check ---
  error = validateOperators(value, rowNum, INVALID);
  if (error) return error;

  // --- Total sets validation ---
  error = validateTotalSets(value, rowNum, INVALID);
  if (error) return error;

  // --- Set validation ---
  const setPattern = /\w{1,40}\d{2}\/\d{2}\/\d{4}$/;
  const setsInParentheses = value.match(/\(([^)]{1,100})\)/g) || [];

  for (const group of setsInParentheses) {
    error = validateSetFormat(
      setPattern,
      validateSetDate,
      group,
      rowNum,
      INVALID,
    );
    if (error) return error;
  }

  return null;
};

export const failedValidation = (setFileError, resolve, header, message) => {
  setFileError({ header, message });
  resolve("invalid");
  return true;
};

export const sortMerchandisingSets = (data) => {
  return [...data].sort((a, b) => {
    const regionCmp = a.region
      ?.toLowerCase()
      .localeCompare(b.region?.toLowerCase());
    if (regionCmp !== 0) return regionCmp;

    const buCmp = a.bu?.toLowerCase().localeCompare(b.bu?.toLowerCase());
    if (buCmp !== 0) return buCmp;

    const nameCmp = a.setName
      ?.toLowerCase()
      .localeCompare(b.setName?.toLowerCase());
    if (nameCmp !== 0) return nameCmp;

    return new Date(a.goLiveDate) - new Date(b.goLiveDate);
  });
};

// paginationHelper
export const getPaginationInfo = (rowData, pageSize, currentPage) => {
  const pageCount = Math.ceil(rowData.length / pageSize);
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === pageCount - 1;

  return {
    pageCount,
    isFirstPage,
    isLastPage,
  };
};
export const getPagedData = (data, pageSize, currentPage) => {
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  return data.slice(startIndex, endIndex);
};

export const convertIntoPercentage = (data) => {
  let perVal = data === null ? 100 : Number(data) * 100;
  return perVal + "%";
};

export const getMSUploadSubTitle = (isImageUpload) => {
  if (isImageUpload) {
    return "MS_Image_Modal_Sub_Title";
  }
  return "MS_Update_Modal_Sub_Title";
};

export const getSurveyDownloadModalContent = ({
  isDownloadOutletList,
  isDownloadCoachingResult,
  t,
}) => {
  if (isDownloadOutletList) {
    return {
      title: t("DownloadOutletList"),
      subTitle: t("SurveyConfiguration"),
    };
  }

  if (isDownloadCoachingResult) {
    return {
      title: t("DownloadLast3MonthsCoachingResults"),
      subTitle: t("CoachingL3MReport"),
    };
  }

  return {
    title: t("Downloadlast3monthResult"),
    subTitle: t("SurveyL3MReport"),
  };
};

export const getOutletValidationFn = ({
  isImageUpload,
  isSurveyOutlet,
  isSosOutlet,
  selectedCountryCode,
}) => {
  if (isImageUpload) {
    return null;
  }

  if (isSurveyOutlet) {
    return (file, setFileError) => ValidateSurveyOutletExcel(file, setFileError, selectedCountryCode);
  }

  if (isSosOutlet) {
    return ValidateSosOutletExcel;
  }

  return ValidateExcel;
};

export const getOutletUploadApi = ({ isSurveyOutlet, isSosOutlet }) => {
  if (isSurveyOutlet) {
    return "surveys/outletmappings/upload";
  }

  if (isSosOutlet) {
    return "sos/outletlist/upload";
  }

  return "posm/outletmapping/upload";
};

// utils/dateUtils.js

export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;

  const clean = dateStr.split("T")[0]; // handles ISO too
  const [year, month, day] = clean.split("-");

  return new Date(Number(year), Number(month) - 1, Number(day));
};

// Format Date → "YYYY-MM-DD" (without UTC conversion)
export const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDate = (dateStr) => {
  if (!dateStr) return null;

  // ✅ Add format validation HERE
  const isValidFormat = /^\d{2}-\d{2}-\d{4}$/;
  if (!isValidFormat.test(dateStr.trim())) return null;

  const parts = dateStr.split("-");
  const [day, month, year] = parts.map(Number);

  // Create UTC date
  const date = new Date(Date.UTC(year, month - 1, day));

  // Validate correctness (handles invalid dates like 31-02-2026)
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
};

export const isWithin6Months = (date) => {
  const today = new Date();

  // Convert today to UTC midnight
  const todayUTC = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );

  const maxDateUTC = new Date(todayUTC);
  maxDateUTC.setUTCMonth(maxDateUTC.getUTCMonth() + 6);

  return date >= todayUTC && date <= maxDateUTC;
};
